/**
 * The consent screen (#373, D30): what DaVinci does with your work, who it may
 * be sent to, and three separate choices.
 *
 * **Feedback and training are never pre-ticked.** Each box starts from the
 * server's record of what the learner has actually decided; for someone who has
 * decided nothing, feedback and training are unticked.
 * ~~Nothing is pre-ticked, for all three.~~ **Amended 2026-10-24 (#451):** the
 * evaluation purpose is pre-ticked for a learner who has decided nothing, at the
 * signup gate and in Settings, and the gate shows it beside feedback. A recorded
 * withdrawal stays unticked. The founder accepts the weaker form of consent for
 * a three-person pilot (#451, #449). The third, training a model, is off, optional, and
 * for now disabled with the reason the server gives: DaVinci has not yet checked
 * each provider's terms on what may be done with a model's output, and will not
 * offer it until it has.
 *
 * **The words are the server's.** At the gate each page renders `statement.gate`
 * (~~the numbered card rendered `statement.summary`~~, now behind Details, English
 * only for now, so each item is `lang="en" dir="ltr"`); the web bundle holds no consent text, because a
 * line it hard-coded would not be covered by `statementVersion`. Details holds
 * purposes, providers and storage, not the summary again. The statement, the providers and their
 * retention, and where the work is kept all come from `GET /consent/statement`
 * and are rendered as received, so the screen cannot say something the
 * recorded statement version does not. A decision quotes the version it was
 * shown; if the statement changed underneath, the server refuses and this screen
 * fetches the new one and asks again, rather than recording agreement to words
 * nobody read.
 *
 * **Withdrawing is as easy as granting, and says what it does.** Unticking a
 * purpose that is currently granted and saving records a withdrawal. For
 * feedback the screen says plainly what that stops and what it cannot reach
 * (anything already sent stays with the provider for the period above).
 *
 * **One page per consent at the gate (#655, #653).** ~~The gate was one screen: the server's six summary lines, then
 * both choices and a Continue button.~~ *Amended 2026-11-04 (#655):* the founder found that it read as the welcome,
 * so the gate is two pages, one consent each, Next at the bottom of each. Page 1 is feedback: needed, never
 * pre-ticked, and Next stays grey until it is ticked. Page 2 is the evaluation purpose: optional, pre-ticked (#451,
 * the reasoning above holds). Both pages are `statement.gate[locale]`, the server's words, covered by the same
 * `statementVersion`; the full text, the summary and the disclosures sit behind Details on the page they belong to,
 * in English only for now. Nothing is posted until the last Next, which saves feedback first and then the
 * evaluation, each call quoting the version. A learner who leaves before it has recorded nothing: an undecided choice
 * counts as not granted. Settings keeps its single form with all three purposes.
 *
 * No percentages, no scores, no progress bar: nothing here is a measurement.
 * Nothing is written to browser storage.
 */

import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ConsentDecisionsResponse, ConsentStatementResponse } from '@davinci/contracts';
import { ApiRequestError } from '../../api/client.js';
import { queryKeys } from '../../api/query-keys.js';
import { useApi } from '../../app/api-context.js';
import { AuthHeroIcon, AuthStage, SignupSteps } from '../../app/auth-layout.js';
import { FailureNotice } from '../../app/failure-notice.js';
import { useLocale, useMessages } from '../../i18n/locale.js';
import { defineMessages } from '../../i18n/messages.js';

type Purpose = ConsentStatementResponse['purposes'][number]['purpose'];
type State = ConsentDecisionsResponse['decisions'][number]['state'];

export interface ConsentScreenProps {
  /**
   * True when this is shown in place of the app because feedback is not granted:
   * the heading and the opening sentence say so. False on the settings screen.
   */
  readonly required: boolean;
  /** True inside the Settings layout, which owns the page's h1: the title is then an h2. */
  readonly nested?: boolean;
}

const messages = defineMessages({
  en: {
    beforeYouStart: 'Before you start',
    next: 'Next',
    back: 'Back',
    savesNote: 'Next saves your choices, with the version of this text you were shown.',
    fullStatement: 'The full statement',
    yourConsent: 'Your consent',
    loading: 'Loading what DaVinci needs to ask you…',
    introRequired: (version: string) =>
      `DaVinci keeps nothing you do until you say yes below. Your choice is recorded with the version of this text you were shown (${version}).`,
    introSettings: (version: string) =>
      `This is what you have agreed to. You can change any of it at any time. Your choice is recorded with the version of this text you were shown (${version}).`,
    inShort: 'In short',
    notSavedHeading: 'Your choices were not saved',
    outdatedChanged:
      'This text changed while you were reading it. It has been reloaded; please read it again and choose.',
    improvementUnavailable: 'Using your work to improve DaVinci is not available yet.',
    couldNotRecord: 'DaVinci could not record that. Nothing has changed. Try again.',
    partlySavedHeading: 'This choice was not saved',
    partlySaved: 'DaVinci recorded your yes to checking your homework, but could not record this choice. Try again.',
    details: 'Details',
    whoMaySee: 'Who may see your work',
    providersIntro:
      'To read your work and write feedback, DaVinci sends it to AI services run by other companies. These are the ones it may use, and what each says about keeping what it receives.',
    whereKept: 'Where it is kept',
    optional: 'Optional',
    needed: 'Needed for DaVinci to store your work',
    pillGranted: 'Granted',
    pillWithdrawn: 'Withdrawn',
    pillUnavailable: 'Not available yet',
    iAgree: (title: string) => `I agree: ${title}`,
    useToImprove: 'Use my work to improve DaVinci',
    improvementNotAsked:
      'Using your work to improve DaVinci is not asked now. You can see it, and change either choice, under Settings → Consent.',
    withdrawingHeading: 'What withdrawing does',
    withdrawingBody:
      'DaVinci will stop using AI services for your work straight away, cancel any imports of yours that have not started, and delete anything it had prepared but not yet sent. It cannot take back what was already sent: that stays with the provider for the period given above. Your stored work stays until you delete it.',
    noFeedbackNote: 'Without this, DaVinci cannot store anything you do, so there is nothing for it to open.',
    seeMyData: 'See or delete my data',
    contactUs: 'Contact',
    saving: 'Saving…',
    save: 'Save my choices',
    savedOpening: 'Saved. Opening DaVinci…',
    saved: 'Saved.',
    stateGranted: 'You have agreed to this.',
    stateOutdated: 'You agreed to an earlier version of this. Please read it again and choose.',
    stateWithdrawn: 'You have withdrawn this.',
    stateNone: 'You have not decided this yet.',
    evalTicked: 'Ticked for you. Saving confirms it.',
    evalTickedOutdated: 'Ticked for you; please read it again. Saving confirms it.',
  },
  he: {
    beforeYouStart: 'לפני שמתחילים',
    next: 'הבא',
    back: 'חזור',
    savesNote: '״הבא״ שומר את הבחירות שלך, יחד עם גרסת הטקסט שהוצגה לך.',
    fullStatement: 'ההצהרה המלאה',
    yourConsent: 'ההסכמה שלך',
    loading: 'טעינת מה ש-DaVinci צריך לשאול אותך…',
    introRequired: (version: string) =>
      `DaVinci לא שומר דבר מהפעילות שלך עד לאישור כאן למטה. הבחירה שלך נרשמת יחד עם גרסת הטקסט שהוצגה לך (${version}).`,
    introSettings: (version: string) =>
      `זה מה שהסכמת לו. אפשר לשנות כל חלק מזה בכל עת. הבחירה שלך נרשמת יחד עם גרסת הטקסט שהוצגה לך (${version}).`,
    inShort: 'בקצרה',
    notSavedHeading: 'הבחירות שלך לא נשמרו',
    outdatedChanged: 'הטקסט הזה השתנה בזמן הקריאה. הוא נטען מחדש. כדאי לקרוא אותו שוב ולבחור.',
    improvementUnavailable: 'השימוש בעבודה שלך כדי לשפר את DaVinci עדיין לא זמין.',
    couldNotRecord: 'DaVinci לא הצליח לרשום את זה. דבר לא השתנה. כדאי לנסות שוב.',
    partlySavedHeading: 'הבחירה הזו לא נשמרה',
    partlySaved: 'ההסכמה לבדיקת שיעורי הבית נרשמה, אבל הבחירה הזו לא. כדאי לנסות שוב.',
    details: 'פרטים',
    whoMaySee: 'מי עשוי לראות את העבודה שלך',
    providersIntro:
      'כדי לקרוא את העבודה שלך ולכתוב משוב, DaVinci שולח אותה לשירותי בינה מלאכותית שמפעילות חברות אחרות. אלה השירותים שהוא עשוי להשתמש בהם, ומה כל אחד מהם אומר על שמירת מה שהוא מקבל.',
    whereKept: 'איפה זה נשמר',
    optional: 'לא חובה',
    needed: 'נדרש כדי ש-DaVinci יוכל לשמור את העבודה שלך',
    pillGranted: 'מאושר',
    pillWithdrawn: 'בוטל',
    pillUnavailable: 'עוד לא זמין',
    iAgree: (title: string) => `הסכמה: ${title}`,
    useToImprove: 'להשתמש בעבודה שלי כדי לשפר את DaVinci',
    improvementNotAsked:
      'השימוש בעבודה שלך כדי לשפר את DaVinci לא נשאל כרגע. אפשר לראות אותו ולשנות כל אחת מהבחירות תחת הגדרות ← הסכמה.',
    withdrawingHeading: 'מה קורה כשמבטלים הסכמה',
    withdrawingBody:
      'DaVinci יפסיק מיד להשתמש בשירותי בינה מלאכותית עבור העבודה שלך, יבטל כל ייבוא שלך שעוד לא התחיל, וימחק כל דבר שהכין אך עוד לא שלח. הוא לא יכול להחזיר מה שכבר נשלח: זה נשאר אצל הספק למשך התקופה שצוינה למעלה. העבודה ששמורה אצלנו נשארת עד למחיקתה.',
    noFeedbackNote: 'בלי זה DaVinci לא יכול לשמור דבר מהפעילות שלך, ולכן אין לו מה לפתוח.',
    seeMyData: 'לצפייה בנתונים או למחיקתם',
    contactUs: 'יצירת קשר',
    saving: 'שמירה…',
    save: 'שמירת הבחירות שלי',
    savedOpening: 'נשמר. DaVinci נפתח…',
    saved: 'נשמר.',
    stateGranted: 'הסכמת לזה.',
    stateOutdated: 'הסכמת לגרסה קודמת של זה. כדאי לקרוא שוב ולבחור.',
    stateWithdrawn: 'ביטלת את ההסכמה לזה.',
    stateNone: 'עוד לא החלטת לגבי זה.',
    evalTicked: 'סימנו עבורך. השמירה מאשרת זאת.',
    evalTickedOutdated: 'סימנו עבורך, כדאי לקרוא שוב. השמירה מאשרת זאת.',
  },
});

type ConsentMessages = (typeof messages)['en'];

// The state as a pill on a purpose card (UX round 3): the board's short words for the two settled states and for a purpose that is not
// offered yet. A state with no short word (not decided, or an earlier version) keeps its sentence, so nothing is lost.
function pillWords(t: ConsentMessages, purpose: Purpose, state: State, unavailable: boolean): string {
  // A decision the learner recorded is said first: a purpose that stopped being offered must not hide it.
  if (state === 'granted') return t.pillGranted;
  if (state === 'withdrawn') return t.pillWithdrawn;
  if (unavailable) return t.pillUnavailable;
  if (purpose === 'system_evaluation' && (state === 'none' || state === 'outdated')) return stateWords(t, purpose, state);
  return stateWords(t, purpose, state);
}

// A pre-ticked evaluation box must not read as already agreed.
function stateWords(t: ConsentMessages, purpose: Purpose, state: State): string {
  if (purpose === 'system_evaluation') {
    if (state === 'none') return t.evalTicked;
    if (state === 'outdated') return t.evalTickedOutdated;
  }
  switch (state) {
    case 'granted':
      return t.stateGranted;
    case 'outdated':
      return t.stateOutdated;
    case 'withdrawn':
      return t.stateWithdrawn;
    case 'none':
      return t.stateNone;
  }
}

export function ConsentScreen({ required, nested = false }: ConsentScreenProps): React.JSX.Element {
  const t = useMessages(messages);
  const api = useApi();
  const statement = useQuery({ queryKey: queryKeys.consentStatement, queryFn: () => api.consentStatement() });
  const decisions = useQuery({ queryKey: queryKeys.consentDecisions, queryFn: () => api.consentDecisions() });

  if (statement.isError) return <FailureNotice error={statement.error} onRetry={() => void statement.refetch()} />;
  if (decisions.isError) return <FailureNotice error={decisions.error} onRetry={() => void decisions.refetch()} />;
  const Title = nested ? 'h2' : 'h1';
  if (!statement.isSuccess || !decisions.isSuccess) {
    if (required) {
      return (
        <AuthStage glyph={'🔒'}>
          <SignupSteps current="consent" />
          <h1>{t.beforeYouStart}</h1>
          <p role="status">{t.loading}</p>
        </AuthStage>
      );
    }
    return (
      <div className="page">
        <Title>{required ? t.beforeYouStart : t.yourConsent}</Title>
        <p role="status">{t.loading}</p>
      </div>
    );
  }

  // Keyed by version, so a changed statement discards the half-made choices that
  // were made against the old words.
  return (
    <ConsentForm
      key={statement.data.statementVersion}
      required={required}
      nested={nested}
      statement={statement.data}
      decisions={decisions.data}
    />
  );
}

/** Who may see the work and where it is kept: the statement's own words, as received. */
function Disclosures({
  statement,
  serverTextIsEnglish = false,
}: {
  readonly statement: ConsentStatementResponse;
  /** At the gate the server's lines are English while the page may be Hebrew; Settings renders as it always has. */
  readonly serverTextIsEnglish?: boolean;
}): React.JSX.Element {
  const t = useMessages(messages);
  const english = serverTextIsEnglish ? ({ lang: 'en', dir: 'ltr' } as const) : {};
  return (
    <>
        <section aria-labelledby="consent-providers">
          <h2 id="consent-providers">{t.whoMaySee}</h2>
          <p>{t.providersIntro}</p>
          <ul className="plain-list">
            {statement.providers.map((provider) => (
              <li key={provider.vendor} {...english}>
                <strong>
                  {provider.vendor} ({provider.product}).
                </strong>{' '}
                {provider.retention}
                {provider.note !== null ? ` ${provider.note}` : null}
              </li>
            ))}
          </ul>
          <ul className="plain-list">
            {statement.providerNotes.map((note) => (
              <li key={note} {...english}>
                {note}
              </li>
            ))}
          </ul>
        </section>

        <section aria-labelledby="consent-storage">
          <h2 id="consent-storage">{t.whereKept}</h2>
          <ul className="plain-list">
            {statement.storage.map((line) => (
              <li key={line} {...english}>
                {line}
              </li>
            ))}
          </ul>
        </section>
    </>
  );
}

interface ConsentFormProps {
  readonly required: boolean;
  readonly nested: boolean;
  readonly statement: ConsentStatementResponse;
  readonly decisions: ConsentDecisionsResponse;
}

function ConsentForm({ required, nested, statement, decisions }: ConsentFormProps): React.JSX.Element {
  const t = useMessages(messages);
  const api = useApi();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const stateOf = (purpose: Purpose): State =>
    decisions.decisions.find((d) => d.purpose === purpose)?.state ?? 'none';

  // What the server holds, as booleans. Seeded once; the form is keyed by
  // statement version, and a save replaces the server data, not these.
  const [chosen, setChosen] = useState<Readonly<Record<Purpose, boolean>>>(() => ({
    feedback: stateOf('feedback') === 'granted',
    // Pre-ticked unless the learner withdrew it (#451); a new version re-ticks it too.
    system_evaluation: ['granted', 'none', 'outdated'].includes(stateOf('system_evaluation')),
    improvement: false,
  }));
  const [saved, setSaved] = useState(false);
  // The gate's page (#655): 0 is feedback, 1 is evaluation. Kept here with `chosen`, so Back keeps the choices and a new
  // statement version (a new key) restarts at the first page.
  const [page, setPage] = useState<0 | 1>(0);
  // The gate asks for feedback and the (pre-ticked) evaluation purpose (#451). Training
  // is not asked there, so the save loop never sends a decision for a purpose the form
  // did not show.
  const shownPurposes = required
    ? statement.purposes.filter((p) => p.purpose === 'feedback' || p.purpose === 'system_evaluation')
    : statement.purposes;
  const summaryRef = useRef<HTMLDivElement>(null);

  // Whether this form's feedback decision was recorded, on any attempt (the form is keyed by statement version, and a
  // retry that then fails has not undone it). `save` posts feedback first, then evaluation, so a failure
  // after that point has changed something, and the notice must not say "Nothing has changed".
  const [feedbackRecorded, setFeedbackRecorded] = useState(false);

  const save = useMutation({
    mutationFn: async () => {
      let latest: ConsentDecisionsResponse | undefined;
      for (const { purpose } of shownPurposes) {
        // At the gate, an unticked feedback box means no evaluation decision at all.
        if (required && purpose === 'system_evaluation' && !chosen.feedback) continue;
        // Training shown disabled is not a choice the learner made.
        if (purpose === 'improvement' && !statement.training.available) continue;
        const state = stateOf(purpose);
        const wasGranted = state === 'granted';
        // A shown purpose not yet decided on and left unticked is a decline: record it,
        // so it is not re-ticked later.
        // Only evaluation: a feedback withdrawal cancels queued imports, and the "What
        // withdrawing does" notice is shown only for a granted feedback.
        const declining = purpose === 'system_evaluation' && (state === 'none' || state === 'outdated') && !chosen[purpose];
        if (chosen[purpose] === wasGranted && !declining) continue;
        // One decision at a time, each quoting the version it was shown.
        latest = await api.recordConsentDecision({
          purpose,
          decision: chosen[purpose] ? 'granted' : 'withdrawn',
          statementVersion: statement.statementVersion,
        });
        if (purpose === 'feedback') setFeedbackRecorded(true);
      }
      return latest;
    },
    onSuccess: (latest) => {
      if (latest !== undefined) queryClient.setQueryData(queryKeys.consentDecisions, latest);
      void queryClient.invalidateQueries({ queryKey: queryKeys.consentDecisions });
      // Work the learner can now do (or can no longer do) is on other screens.
      void queryClient.invalidateQueries({ queryKey: queryKeys.imports });
      setSaved(true);
      // #648: the first yes to feedback continues into the welcome steps (Moodle, Courses). A returning learner who is
      // only re-asked after a changed statement, or who said no, is not pushed there.
      if (required && stateOf('feedback') === 'none' && chosen.feedback) void navigate('/welcome', { replace: true });
    },
    onError: (error) => {
      setSaved(false);
      // The statement changed under the form: fetch the new words and ask again.
      if (error instanceof ApiRequestError && error.code === 'consent_statement_outdated') {
        void queryClient.invalidateQueries({ queryKey: queryKeys.consentStatement });
        void queryClient.invalidateQueries({ queryKey: queryKeys.consentDecisions });
      }
    },
  });

  const outdated = save.error instanceof ApiRequestError && save.error.code === 'consent_statement_outdated';
  const feedbackWasGranted = stateOf('feedback') === 'granted';
  const withdrawingFeedback = feedbackWasGranted && !chosen.feedback;
  const noFeedback = !chosen.feedback && !feedbackWasGranted;

  useEffect(() => {
    if (save.isError) summaryRef.current?.focus();
  }, [save.isError, save.submittedAt]);

  function toggle(purpose: Purpose, value: boolean): void {
    setSaved(false);
    setChosen((current) => ({ ...current, [purpose]: value }));
  }

  if (required) {
    return (
      <GatePages
        t={t}
        statement={statement}
        page={page}
        setPage={setPage}
        chosen={chosen}
        toggle={toggle}
        stateOf={stateOf}
        save={{ isPending: save.isPending, isError: save.isError, error: save.error, mutate: () => save.mutate() }}
        saved={saved}
        outdated={outdated}
        feedbackRecorded={feedbackRecorded}
        summaryRef={summaryRef}
      />
    );
  }

  return (
    <div className="page">
      {nested ? <h2>{required ? t.beforeYouStart : t.yourConsent}</h2> : <h1>{required ? t.beforeYouStart : t.yourConsent}</h1>}
      <p>{required ? t.introRequired(statement.statementVersion) : t.introSettings(statement.statementVersion)}</p>

      <section className="consent-summary" aria-labelledby="consent-summary">
        <h2 id="consent-summary">{t.inShort}</h2>
        <ul className="plain-list">
          {statement.summary.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      </section>

      {save.isError ? (
        <div className="notice notice-error" role="alert" tabIndex={-1} ref={summaryRef}>
          <h2>{t.notSavedHeading}</h2>
          <p>
            {outdated
              ? t.outdatedChanged
              : save.error instanceof ApiRequestError && save.error.code === 'training_terms_unverified'
                ? t.improvementUnavailable
                : t.couldNotRecord}
          </p>
        </div>
      ) : null}

      <details>
        <summary>{t.details}</summary>
        <Disclosures statement={statement} />
      </details>

      <form
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate();
        }}
      >
        {shownPurposes.map((purpose) => {
          const isTraining = purpose.purpose === 'improvement';
          const disabled = isTraining && !statement.training.available;
          const id = `consent-${purpose.purpose}`;
          return (
            <fieldset className="field-group" key={purpose.purpose}>
              <legend>{purpose.title}</legend>
              <p>{purpose.text}</p>
              <div className="consent-pills">
                <span className="consent-pill" data-kind={purpose.optional ? 'optional' : 'needed'}>
                  {purpose.optional ? t.optional : t.needed}
                </span>
                <span className="consent-pill" data-state={disabled ? 'unavailable' : stateOf(purpose.purpose)}>
                  {pillWords(t, purpose.purpose, stateOf(purpose.purpose), disabled)}
                </span>
              </div>
              <div className="field">
                <label htmlFor={id}>
                  <input
                    id={id}
                    type="checkbox"
                    checked={disabled ? false : chosen[purpose.purpose]}
                    disabled={disabled}
                    aria-describedby={disabled ? `${id}-reason` : undefined}
                    onChange={(event) => toggle(purpose.purpose, event.target.checked)}
                  />{' '}
                  {isTraining ? t.useToImprove : t.iAgree(purpose.title)}
                </label>
                {disabled ? (
                  <p className="field-hint" id={`${id}-reason`}>
                    {statement.training.unavailableReason}
                  </p>
                ) : null}
              </div>
            </fieldset>
          );
        })}

        {required ? (
          <p className="field-hint">
            {t.improvementNotAsked}
          </p>
        ) : null}

        {withdrawingFeedback ? (
          <div className="notice" role="note">
            <h2>{t.withdrawingHeading}</h2>
            <p>{t.withdrawingBody}</p>
          </div>
        ) : null}

        {required && noFeedback ? (
          <p className="page-note">
            {t.noFeedbackNote}
          </p>
        ) : null}

        {required ? (
          <p className="page-note">
            <Link to="/settings/data">{t.seeMyData}</Link> · <Link to="/settings/contact">{t.contactUs}</Link>
          </p>
        ) : null}

        <button type="submit" disabled={save.isPending}>
          {save.isPending ? t.saving : t.save}
        </button>
        {saved ? (
          <p role="status">
            {required && chosen.feedback ? t.savedOpening : t.saved}
          </p>
        ) : null}
      </form>
    </div>
  );
}

type ChosenPurposes = Readonly<Record<Purpose, boolean>>;

interface GatePagesProps {
  readonly t: ConsentMessages;
  readonly statement: ConsentStatementResponse;
  readonly page: 0 | 1;
  readonly setPage: (page: 0 | 1) => void;
  readonly chosen: ChosenPurposes;
  readonly toggle: (purpose: Purpose, value: boolean) => void;
  readonly stateOf: (purpose: Purpose) => State;
  readonly save: {
    readonly isPending: boolean;
    readonly isError: boolean;
    readonly error: Error | null;
    readonly mutate: () => void;
  };
  readonly saved: boolean;
  readonly outdated: boolean;
  readonly feedbackRecorded: boolean;
  readonly summaryRef: React.RefObject<HTMLDivElement | null>;
}

const ICON_PROPS = {
  width: 18,
  height: 18,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
} as const;

/** The board's row icons (RegConsent, RegConsentEval). */
const ROW_ICONS = {
  arrow: (
    <svg {...ICON_PROPS}>
      <path d="M7 17L17 7" />
      <path d="M9 7h8v8" />
    </svg>
  ),
  clock: (
    <svg {...ICON_PROPS}>
      <circle cx="12" cy="12" r="8" />
      <path d="M12 8v4l3 2" />
    </svg>
  ),
  eye: (
    <svg {...ICON_PROPS}>
      <path d="M3 12s3.5-6 9-6 9 6 9 6-3.5 6-9 6-9-6-9-6z" />
      <circle cx="12" cy="12" r="2.5" />
    </svg>
  ),
  shield: (
    <svg {...ICON_PROPS}>
      <path d="M12 3l7 3v5c0 5-3 8-7 10-4-2-7-5-7-10V6z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  ),
  magnifier: (
    <svg {...ICON_PROPS}>
      <circle cx="11" cy="11" r="7" />
      <path d="M16 16l5 5" />
    </svg>
  ),
} as const;

type RowIcon = keyof typeof ROW_ICONS;
type RowCourse = 'teal' | 'ochre' | 'plum' | 'neutral';

/** Per page, the icon and tint of each row in order, as on the boards. */
const ROW_STYLES: readonly (readonly (readonly [RowIcon, RowCourse])[])[] = [
  [
    ['arrow', 'teal'],
    ['clock', 'ochre'],
    ['eye', 'plum'],
    ['shield', 'neutral'],
  ],
  [
    ['magnifier', 'teal'],
    ['clock', 'ochre'],
    ['shield', 'plum'],
  ],
];

const HERO_ICONS = [
  <AuthHeroIcon key="tick">
    <path d="M7 3h7l4 4v14H7z" />
    <path d="M10 13l2 2 3-4" />
  </AuthHeroIcon>,
  <AuthHeroIcon key="magnifier">
    <circle cx="11" cy="11" r="7" />
    <path d="M16 16l5 5" />
  </AuthHeroIcon>,
] as const;

/**
 * The gate (#655): one page per consent. Page 1 posts nothing; the last Next on page 2 is the form's submit and saves
 * both choices. Every word on a page is `statement.gate[locale]`, so the version a decision quotes covers it.
 */
function GatePages({
  t,
  statement,
  page,
  setPage,
  chosen,
  toggle,
  stateOf,
  save,
  saved,
  outdated,
  feedbackRecorded,
  summaryRef,
}: GatePagesProps): React.JSX.Element {
  const { locale } = useLocale();
  const pages = statement.gate[locale];
  const current = pages[page];
  const [showNote, setShowNote] = useState(false);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const shownPage = useRef(page);

  // Focus follows Next and Back to the new heading; it does not fire on first mount. Focus also scrolls it into view.
  useEffect(() => {
    if (shownPage.current === page) return;
    shownPage.current = page;
    headingRef.current?.focus();
  }, [page]);

  if (current === undefined) {
    return (
      <AuthStage glyph={HERO_ICONS[0]}>
        <SignupSteps current="consent" />
        <h1>{t.beforeYouStart}</h1>
      </AuthStage>
    );
  }

  const evaluationPurpose = statement.purposes.find((p) => p.purpose === 'system_evaluation');
  const feedbackPurpose = statement.purposes.find((p) => p.purpose === 'feedback');
  const feedbackState = stateOf('feedback');
  const evaluationState = stateOf('system_evaluation');
  const rowStyles = ROW_STYLES[page] ?? [];
  // An outdated evaluation says the text changed, on its own line under the page's hint.
  const evaluationHint: readonly string[] =
    evaluationState === 'withdrawn'
      ? [t.stateWithdrawn]
      : evaluationState === 'outdated'
        ? [...(current.hint !== null ? [current.hint] : []), t.stateOutdated]
        : current.hint !== null
          ? [current.hint]
          : [];
  const partlySaved = feedbackRecorded && !outdated;

  const heading = (
    <h1 tabIndex={-1} ref={headingRef}>
      {current.title}
    </h1>
  );

  const rows = (
    <ul className="auth-lines">
      {current.rows.map((row, index) => {
        const [icon, course] = rowStyles[index] ?? (['shield', 'neutral'] as const);
        return (
          <li key={row.heading} data-course={course}>
            <span className="auth-line-mark" aria-hidden="true">
              {ROW_ICONS[icon]}
            </span>
            <span className="auth-line-text">
              <strong>{row.heading}</strong>
              <span>{row.text}</span>
            </span>
          </li>
        );
      })}
    </ul>
  );

  if (page === 0) {
    return (
      <AuthStage glyph={HERO_ICONS[0]}>
        <SignupSteps current="consent" />
        <p className="auth-page-label">{current.label}</p>
        {heading}
        {current.lead !== null ? <p className="auth-lead">{current.lead}</p> : null}
        {rows}

        <details className="auth-details">
          <summary>{t.details}</summary>
          <h2>{t.fullStatement}</h2>
          {statement.summary.map((line) => (
            <p key={line} lang="en" dir="ltr">
              {line}
            </p>
          ))}
          {feedbackPurpose !== undefined ? (
            <section aria-label={feedbackPurpose.title}>
              <h3>{feedbackPurpose.title}</h3>
              <p lang="en" dir="ltr">
                {feedbackPurpose.text}
              </p>
            </section>
          ) : null}
          <Disclosures statement={statement} serverTextIsEnglish />
          <p>{t.introRequired(statement.statementVersion)}</p>
        </details>

        <div className="auth-form">
          <label className="auth-choice" htmlFor="consent-feedback">
            <span>{current.choice}</span>
            <input
              id="consent-feedback"
              type="checkbox"
              checked={chosen.feedback}
              aria-describedby={feedbackState !== 'none' ? 'consent-feedback-hint' : undefined}
              onChange={(event) => {
                setShowNote(false);
                toggle('feedback', event.target.checked);
              }}
            />
          </label>
          {feedbackState !== 'none' ? (
            <p className="field-hint" id="consent-feedback-hint">
              {stateWords(t, 'feedback', feedbackState)}
            </p>
          ) : null}
          <p className="field-hint">
            <Link to="/settings/data">{t.seeMyData}</Link> {'·'} <Link to="/settings/contact">{t.contactUs}</Link>
          </p>

          {showNote && !chosen.feedback ? (
            <p role="status" className="auth-note">
              {t.noFeedbackNote}
            </p>
          ) : null}

          <button
            className="auth-submit"
            type="button"
            aria-disabled={chosen.feedback ? undefined : 'true'}
            onClick={() => {
              if (chosen.feedback) setPage(1);
              else setShowNote(true);
            }}
          >
            {t.next}
          </button>
        </div>
      </AuthStage>
    );
  }

  return (
    <AuthStage glyph={HERO_ICONS[1]}>
      <SignupSteps current="consent" />
      <p className="auth-page-label">
        <span>{current.label}</span>
        <button className="auth-back" type="button" onClick={() => setPage(0)}>
          {t.back}
        </button>
      </p>
      {heading}
      {current.lead !== null ? <p className="auth-lead">{current.lead}</p> : null}
      {rows}

      {save.isError ? (
        <div className="notice notice-error" role="alert" tabIndex={-1} ref={summaryRef}>
          <h2>{partlySaved ? t.partlySavedHeading : t.notSavedHeading}</h2>
          <p>
            {outdated
              ? t.outdatedChanged
              : partlySaved
                ? t.partlySaved
                : save.error instanceof ApiRequestError && save.error.code === 'training_terms_unverified'
                  ? t.improvementUnavailable
                  : t.couldNotRecord}
          </p>
        </div>
      ) : null}

      <details className="auth-details">
        <summary>{t.details}</summary>
        {evaluationPurpose !== undefined ? (
          <section aria-label={evaluationPurpose.title}>
            <h3>{evaluationPurpose.title}</h3>
            <p lang="en" dir="ltr">
              {evaluationPurpose.text}
            </p>
          </section>
        ) : null}
      </details>

      <form
        className="auth-form"
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate();
        }}
      >
        <label className="auth-choice" htmlFor="consent-system_evaluation">
          <span>{current.choice}</span>
          <input
            id="consent-system_evaluation"
            type="checkbox"
            checked={chosen.system_evaluation}
            aria-describedby={evaluationHint.length > 0 ? 'consent-system_evaluation-hint' : undefined}
            onChange={(event) => toggle('system_evaluation', event.target.checked)}
          />
        </label>
        {evaluationHint.length > 0 ? (
          <p className="field-hint" id="consent-system_evaluation-hint">
            {evaluationHint.map((line) => (
              <span className="field-hint-line" key={line}>
                {line}
              </span>
            ))}
          </p>
        ) : null}

        <p className="field-hint">{t.savesNote}</p>
        <button className="auth-submit" type="submit" disabled={save.isPending}>
          {save.isPending ? t.saving : t.next}
        </button>
        {saved ? <p role="status">{chosen.feedback ? t.savedOpening : t.saved}</p> : null}
      </form>
    </AuthStage>
  );
}
